import { DollarSign, GripVertical, Move, Package, Trash2 } from "lucide-react";
import React from "react";

export const BlindDiagram = React.memo(({ components }) => {
    const componentHeight = 20; // Height of each component in the preview
    const totalHeight = 150;
    const width = 200;

    return (
        <svg viewBox={`0 0 ${width} ${totalHeight}`} className="w-100 h-auto">
            <defs>
                <linearGradient id="blindGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#f0f0f8', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#d9e2ec', stopOpacity: 1 }} />
                </linearGradient>
            </defs>
            <rect x="50" y="20" width="100" height="110" fill="url(#blindGradient)" stroke="#c0cad5" strokeWidth="0.5" rx="2" ry="2" />
            <rect x="48" y="10" width="104" height="12" fill="#d1d5db" stroke="#b0b8c4" strokeWidth="0.5" rx="1.5" ry="1.5" />
            <line x1="180" y1="30" x2="180" y2="50" stroke="#a0aec0" strokeWidth="1" />
            <circle cx="180" cy="55" r="2.5" fill="#a0aec0" />

            {/* Render components as horizontal bars in the blind */}
            {components.map((comp, index) => {
                const yPosition = 30 + index * componentHeight;
                return (
                    <rect
                        key={comp.instanceId}
                        x={50}
                        y={yPosition}
                        width={100}
                        height={componentHeight - 2}
                        fill={`hsl(${index * 40}, 70%, 80%)`} // Different colors for each component
                        stroke="#a0aec0"
                        strokeWidth="0.5"
                    />
                );
            })}
        </svg>
    );
});

export const DraggableComponent = ({ component, onDragStart }) => {
    return (
        <div
            draggable
            onDragStart={(e) => onDragStart(e, component)}
            className="d-flex align-items-center justify-content-between p-3 mb-2 bg-white rounded shadow-sm cursor-move"
        >
            <div className="d-flex align-items-center">
                <GripVertical size={20} className="me-2 text-secondary" />
                <Package size={20} className="me-2 text-primary" />
                <span className="fw-medium text-dark">{component.name}</span>
            </div>
            <div className="d-flex align-items-center">
                <DollarSign className="me-1 text-success" size={16} />
                <span className="text-sm text-dark">{parseFloat(component.cost).toFixed(2)}</span>
            </div>
        </div>
    );
};

export const DroppedComponentItem = ({ component, onRemove, onComponentDragStart, isDragging, style }) => {
    return (
        <div
            style={{
                position: 'absolute',
                left: `${component.x}px`,
                top: `${component.y}px`,
                transform: 'translate(-50%, -50%)',
                zIndex: isDragging ? 100 : 10,
                cursor: 'grab',
                maxWidth: '150px',
                ...style,
            }}
            className="p-2 bg-white border rounded-lg w-auto position-relative"
            onMouseDown={(e) => {
                if (e.target.closest('button')) return;
                if (e.button === 0) {
                    onComponentDragStart(e, component.instanceId);
                }
            }}
            onTouchStart={(e) => {
                if (e.target.closest('button')) return;
                onComponentDragStart(e, component.instanceId);
            }}
        >
            <div className="d-flex flex-column align-items-center">
                <Move size={10} className="text-primary mb-1 opacity-75" />
                <Package size={14} className="mb-1 text-primary" />
                <span className="fs-6 fw-semibold text-dark" style={{ overflowWrap: 'break-word' }}>{component.name}</span>
                <div className="d-flex align-items-center mt-1">
                    <DollarSign size={10} className="me-1 text-success" />
                    <span className="fs-7 text-dark">{parseFloat(component.cost).toFixed(2)}</span>
                </div>
            </div>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onRemove(component.instanceId);
                }}
                onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                }}
                onTouchStart={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                }}
                className="position-absolute btn btn-sm d-flex align-items-center justify-content-center"
                style={{
                    top: '-8px',
                    right: '-8px',
                    width: '24px',
                    height: '24px',
                    minWidth: '24px',
                    minHeight: '24px',
                    padding: '0',
                    backgroundColor: '#dc3545',
                    border: '1px solid #dc3545',
                    borderRadius: '50%',
                    color: 'white',
                    zIndex: 1000,
                    transition: 'all 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#b02a37';
                    e.target.style.borderColor = '#b02a37';
                }}
                onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#dc3545';
                    e.target.style.borderColor = '#dc3545';
                }}
                title="Remove component"
                aria-label="Remove component from blind"
            >
                <Trash2 size={12} />
            </button>
        </div>
    );
};